import { isEmptyObj } from "@dhx/ts-common/core";
import { IDataItem } from "@dhx/ts-data";
import { ShapesCollection } from "../../ShapesCollection";
import { IDiagramConfig } from "../../types";
import { directLinkPath } from "../linkPaths";
import { setHeaderColor } from "../placement";

interface IRootPosition {
	id: string;
	x: number;
	y: number;
}

function getAllUniqueShapeIds(treeMatrix: string[][]): string[] {
	const branchIds: string[] = [];

	treeMatrix.forEach(branch => {
		branch.forEach(shapeId => {
			if (!branchIds.includes(shapeId) && shapeId) {
				branchIds.push(shapeId);
			}
		});
	});

	return branchIds;
}

function getMaxBranchHeight(branch: string[], data: ShapesCollection): number {
	let maxHeight = 0;

	for (let index = 0; index < branch.length; index++) {
		const height = data.getItem(branch[1])?.height;
		if (height && height > maxHeight) maxHeight = height;
	}

	return maxHeight;
}

function getBranchWidth(branch: string[], data: ShapesCollection, config: IDiagramConfig): number {
	const allWidths = [];

	if (branch.length > 1) {
		branch.forEach((id, index) => {
			const shape = data.getItem(id);
			if (index !== 0 && !shape.hidden && data.getItem(shape.parent).open) {
				allWidths.push(shape.width + config.margin.itemX);
			}
		});
	}
	if (!allWidths.length) {
		allWidths.push(0);
	}
	return allWidths.reduce((acc, curr) => acc + curr);
}

function getTreeWidth(treeMatrix: string[][], data: ShapesCollection, config: IDiagramConfig): number {
	const allWidths = [];

	treeMatrix.forEach(branch => {
		allWidths.push(getBranchWidth(branch, data, config));
	});

	return Math.max(...allWidths);
}

function getMatrixTree(data: ShapesCollection, tree: string[][]): string[][] {
	const matrixTree: string[][] = [];
	function traverse(tree) {
		tree.forEach(branchIds => {
			const shape = data.getItem(branchIds[1]);
			if (shape.$connection) {
				traverse(shape.$connection);
			} else {
				let currentParent =
					(shape.parent && shape.parent.toString()) || data.getItem(data.getId(0)).id;
				const currentBranch = [];
				currentBranch.push(shape.id.toString());

				while (currentParent) {
					currentBranch.push(currentParent.toString());
					currentParent = data.getItem(currentParent).parent;
				}

				currentBranch.reverse();
				matrixTree.push(currentBranch);
			}
		});
		return matrixTree;
	}

	traverse(tree);

	return matrixTree;
}

function divideRootDirectChildren(root: IDataItem, data: ShapesCollection, { typeConfig }: IDiagramConfig) {
	const rootChild = root.$connection;
	const leftTree = [];
	const rightTree = [];

	const isLeftSide = typeConfig?.side?.hasOwnProperty("left") && typeConfig.side.left.length;
	const isRightSide = typeConfig?.side?.hasOwnProperty("right") && typeConfig.side.right.length;

	if (typeConfig && (typeConfig?.direction === "left" || typeConfig?.direction === "right")) {
		rootChild.forEach(childArray => {
			if (typeConfig.direction === "left") {
				leftTree.push(childArray);
			} else {
				rightTree.push(childArray);
			}
		});
	} else if (typeConfig && (isLeftSide || isRightSide)) {
		rootChild.forEach((childArray, index) => {
			if (isLeftSide && typeConfig.side.left.includes(childArray[1])) {
				leftTree.push(childArray);
			} else if (isRightSide && typeConfig.side.right.includes(childArray[1])) {
				rightTree.push(childArray);
			} else {
				index % 2 ? leftTree.push(childArray) : rightTree.push(childArray);
			}
		});
	} else {
		const rightCount = Math.floor(rootChild.length / 2);
		rootChild.forEach((childArray, index) => {
			const shape = data.getItem(childArray[1]);
			if (shape.dir === "verticalLeft") {
				leftTree.push(childArray);
			} else if (shape.dir === "verticalRight") {
				rightTree.push(childArray);
			} else {
				index < rightCount ? rightTree.push(childArray) : leftTree.push(childArray);
			}
		});
	}

	return { leftTree, rightTree };
}

function setupNodesSizes(
	branches: string[][],
	config: IDiagramConfig,
	data: ShapesCollection,
	dx: number,
	reverse: boolean,
	rootElementPositions: IRootPosition[]
) {
	const unique = getAllUniqueShapeIds(branches);

	let currentY = 0;
	const { itemX, itemY } = config.margin;

	const nodesWithSizesArray = branches.map(branch => {
		const widthArray = [];
		const heightArray = [];

		let currentX = dx;

		branch.forEach(shape => {
			if (!reverse) {
				const item = { ...data.getItem(shape) };

				widthArray.push(currentX);
				heightArray.push(currentY);
				currentX += +itemX + +item.width;
			} else {
				const item = { ...data.getItem(shape) };
				if (item.parent) {
					currentX -= +itemX + +item.width;
				}
				widthArray.push(currentX);
				heightArray.push(currentY);
			}
		});

		const result = branch.map((node, indx) => {
			const item = { ...data.getItem(node) };

			item._x = widthArray[indx];
			item._y = heightArray[indx];

			return item;
		});

		const maxHeight = getMaxBranchHeight(branch, data);
		currentY += itemY + maxHeight;

		return result;
	});

	const coordinatesArray = unique.map(id => {
		const arr = [];
		nodesWithSizesArray.forEach(row => {
			const item = row.find(item => item.id === id, true);
			if (item) arr.push({ x: item._x, y: item._y });
		});

		const x = arr[0].x;
		const y = arr.reduce((acc, curr) => acc + curr.y, 0) / arr.length;

		return { id, x, y };
	});

	rootElementPositions.push(coordinatesArray[0]);

	unique.forEach((id, indx) => {
		if (id) {
			const item = data.getItem(id);

			item.x = coordinatesArray[indx].x;
			item.y = coordinatesArray[indx].y;

			delete item._x;
			delete item._y;
		}
	});
}

function hCorrection(branches: string[][], diff: number, data: ShapesCollection) {
	const unique = getAllUniqueShapeIds(branches);

	unique.forEach(id => {
		if (id && data.getIndex(id) !== 0) {
			const item = data.getItem(id);
			item.y += diff;
		}
	});
}

function setupLinks(
	treeIdsArray: string[],
	dir: "verticalLeft" | "verticalRight",
	data: ShapesCollection,
	config: IDiagramConfig,
	root: IDataItem
) {
	treeIdsArray.forEach(id => {
		const item = data.getItem(id);
		const kids = item.$connection;

		if (item.id !== root.id) item.dir = dir;

		if (kids) {
			item.$count = kids.length;
			kids.forEach(kid => {
				if (treeIdsArray.includes(kid[1])) {
					const sub = data.getItem(kid[1]);
					const link = data.getItem(kid[0]);

					link.connectType = "curved";
					directLinkPath(link, item, sub, config);
				}
			});
		}
	});
}

function verticalCorrection(
	rootPosition: IRootPosition[],
	rightBranches: string[][],
	leftBranches: string[][],
	data: ShapesCollection,
	root: IDataItem
) {
	if (rootPosition[1] && rootPosition[0].y > rootPosition[1].y) {
		hCorrection(rightBranches, rootPosition[0].y - rootPosition[1].y, data);
		root.y = rootPosition[0].y;
	} else if (rootPosition[1] && rootPosition[0].y < rootPosition[1].y) {
		hCorrection(leftBranches, rootPosition[1].y - rootPosition[0].y, data);
		root.y = rootPosition[1].y;
	}
}

const getUniqueCallback = (value: string[], index: number, self: string[][]) => {
	return self.indexOf(value) === index;
};

function removeRepetitiveBranches(matrix) {
	return matrix
		.map(item => JSON.stringify(item))
		.filter(getUniqueCallback)
		.map(item => JSON.parse(item));
}

function removeClosedBranches(data: ShapesCollection, branches: string[][]) {
	const isClosed = [];

	const filteredBranches = branches.map(branch => {
		let restIgnore = false;

		return branch
			.map(id => {
				if (restIgnore) {
					return;
				} else {
					const item = data.getItem(id);

					if (item.open === false) {
						isClosed.push(id);
						restIgnore = true;
					}

					return id;
				}
			})
			.filter(item => item);
	});

	return removeRepetitiveBranches(filteredBranches);
}

function setupBranch(
	data: ShapesCollection,
	config: IDiagramConfig,
	tree,
	branchHorizontalOffset: number,
	rootPosition: IRootPosition[],
	isLeft?: boolean
) {
	if (isLeft) {
		const allLeftBranches = removeClosedBranches(data, getMatrixTree(data, tree));
		branchHorizontalOffset = getTreeWidth(allLeftBranches, data, config);
		setupNodesSizes(allLeftBranches, config, data, branchHorizontalOffset, true, rootPosition);

		return { allLeftBranches, leftBranchWidth: branchHorizontalOffset };
	} else {
		const allRightBranches = removeClosedBranches(data, getMatrixTree(data, tree));
		setupNodesSizes(allRightBranches, config, data, branchHorizontalOffset, false, rootPosition);

		return { allRightBranches };
	}
}

export function placeMindmap(data: ShapesCollection, config: IDiagramConfig) {
	const roots = data.getRoots();
	if (roots.length !== 1) return;

	const root = data.getItem(roots[0]);

	if (root?.$connection) {
		const { leftTree, rightTree } = divideRootDirectChildren(root, data, config);
		const rootElementPositions = [];

		let allRightBranches;
		let allLeftBranches;
		let branchHorizontalOffset = 0;

		[leftTree, rightTree].forEach((tree, treeIndx) => {
			if (tree.length) {
				if (treeIndx === 0) {
					const res = setupBranch(
						data,
						config,
						tree,
						branchHorizontalOffset,
						rootElementPositions,
						true
					);

					allLeftBranches = res.allLeftBranches;
					branchHorizontalOffset = res.leftBranchWidth;
				} else {
					const res = setupBranch(data, config, tree, branchHorizontalOffset, rootElementPositions);
					allRightBranches = res.allRightBranches;
				}
			}
		});

		if (leftTree.length && rightTree.length) {
			verticalCorrection(rootElementPositions, allRightBranches, allLeftBranches, data, root);
		}

		const rootDir = [];

		[leftTree, rightTree].forEach((tree, treeIndx) => {
			if (tree.length) {
				const dir = treeIndx === 0 ? "verticalLeft" : "verticalRight";
				const branches = dir === "verticalLeft" ? allLeftBranches : allRightBranches;

				!rootDir.includes(dir) && rootDir.push(dir);
				root.dir = rootDir;

				tree.forEach(childIds => {
					const item = data.getItem(childIds[1]);
					item.dir = dir;
				});

				setupLinks(getAllUniqueShapeIds(branches), dir, data, config, root);
			}
		});

		if (Array.isArray(root.dir)) {
			if (isEmptyObj(root.openDir)) root.openDir = {};

			if (root.dir.includes("verticalLeft") && !Object.keys(root.openDir).includes("left")) {
				root.openDir.left = typeof root.open === "undefined" ? true : root.open;
			}
			if (root.dir.includes("verticalRight") && !Object.keys(root.openDir).includes("right")) {
				root.openDir.right = typeof root.open === "undefined" ? true : root.open;
			}
			if (root.open === false) root.openDir = { right: false, left: false };
		}

		root.$connection.forEach(childId => {
			const childDir = data.getItem(childId[1]).dir.toLowerCase();

			for (const [key, value] of Object.entries(root.openDir)) {
				if (childDir.includes(key) && !value) {
					data.update(
						childId[1],
						{
							hidden: true,
						},
						true
					);
				}
			}
		});
	} else {
		root.x = 0;
		root.y = 0;
	}

	setHeaderColor(data);
}
